import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBacktestJobs, getBacktestJob, postBacktestTarget, postRuleBacktest, deleteBacktestJob } from '../services/api';
import NavRail from '../components/NavRail';
import BacktestMatrixView from '../components/BacktestMatrixView';
import CopyBtn from '../components/CopyBtn';

/* ============================================================
   BACKTESTS — file + historique + détail (reconstruction Helius slot-1 × TP)
   Source : table PG backtest_jobs via /api/backtest/jobs.
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{--bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;}
.bt *{box-sizing:border-box;margin:0;padding:0}
.bt,.bt *{font-family:'JetBrains Mono',ui-monospace,monospace;}
.bt{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.bt .sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.bt .sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.bt .sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.bt .sb-item:hover{background:var(--surface);color:var(--text);}
.bt .sb-item.on{background:var(--surface);color:var(--cyan);box-shadow:inset 2px 0 0 var(--cyan);}
.bt .sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.bt .sb-item:hover .tip{opacity:1;}
.bt .sb-spacer{flex:1;}
.bt .main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.bt .top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.bt .top-title{font-size:14px;font-weight:800;letter-spacing:.04em;color:var(--text);}
.bt .top-spacer{flex:1;}
.bt .clock{font-size:11px;color:var(--text2);text-align:right;}
.bt .clock b{color:var(--text);font-weight:600;}
.bt .page{flex:1;overflow:auto;padding:16px;}
.bt .bar-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;gap:12px;flex-wrap:wrap;}
.bt .h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.bt .sort-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.bt .sort-lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.bt .sort-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 11px;border-radius:6px;cursor:pointer;letter-spacing:.04em;transition:all .12s;}
.bt .sort-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.bt .sort-btn:hover{color:var(--text);}
.bt .tbl-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.bt table{width:100%;border-collapse:collapse;}
.bt thead th{font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 12px;font-weight:600;background:#0e1014;}
.bt tbody td{padding:9px 12px;font-size:11.5px;border-top:1px solid #16191f;vertical-align:middle;}
.bt tbody tr:hover{background:var(--surface2);}
.bt .addr{font-size:10px;color:var(--text2);font-family:'JetBrains Mono',monospace;}
.bt .badge{font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:5px;display:inline-block;}
.bt .badge.pending{background:rgba(255,176,32,.16);color:var(--amber);}
.bt .badge.running{background:rgba(34,211,238,.16);color:var(--cyan);}
.bt .badge.done{background:rgba(0,224,138,.16);color:var(--green);}
.bt .badge.error{background:rgba(255,59,92,.16);color:var(--red);}
.bt .muted{color:var(--text2);}
.bt .loading{color:var(--text2);padding:24px;font-size:12.5px;}
.bt .nodata{display:flex;align-items:center;justify-content:center;min-height:80px;font-size:11.5px;color:var(--text2);border:1px dashed var(--border);border-radius:8px;}
.bt .err-banner{font-size:11px;color:var(--amber);margin-bottom:12px;padding:8px 12px;border-radius:8px;background:rgba(255,176,32,.10);border:1px solid rgba(255,176,32,.35);}

/* modale détail — réutilise les classes .tdp-* du panel */
.tdp-ovl{position:fixed;inset:0;background:rgba(0,0,0,.66);backdrop-filter:blur(2px);z-index:200;display:flex;align-items:center;justify-content:center;padding:16px;font-family:'JetBrains Mono',ui-monospace,monospace;color:var(--text);}
.tdp-modal{width:92vw;max-width:1100px;height:88vh;background:var(--bg);border:1px solid var(--border);border-radius:14px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 70px rgba(0,0,0,.6);}
.tdp-top{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;flex-wrap:wrap;}
.tdp-back{background:transparent;border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:13px;padding:6px 12px;border-radius:8px;cursor:pointer;}
.tdp-title{font-size:13px;font-weight:700;}
.tdp-sub{font-size:11px;color:var(--text2);}
.tdp-sp{flex:1;}
.tdp-body{flex:1;overflow:auto;padding:14px 16px;}
.tdp-score{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:14px;}
@media(max-width:680px){.tdp-score{grid-template-columns:repeat(3,1fr);}.tdp-modal{width:100vw;height:100vh;border-radius:0;}.tdp-ovl{padding:0;}}
.tdp-kpi{background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:9px 10px;}
.tdp-kpi .k{font-size:9px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;}
.tdp-kpi .v{font-size:16px;font-weight:800;margin-top:3px;}
.tdp-ctrl{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:8px;}
.tdp-ctrl .lbl{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-right:2px;}
.tdp-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 10px;border-radius:6px;cursor:pointer;letter-spacing:.03em;}
.tdp-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.tdp-tbl{width:100%;border-collapse:collapse;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.tdp-tbl th{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 10px;background:#0e1014;}
.tdp-tbl th.num{text-align:right;}
.tdp-tbl td{padding:8px 10px;font-size:11.5px;border-top:1px solid #16191f;}
.tdp-tbl td.num{text-align:right;font-variant-numeric:tabular-nums;}
.tdp-mint{font-size:10px;color:var(--text2);cursor:pointer;}
.tdp-tl{font-size:9px;font-weight:700;text-decoration:none;padding:2px 5px;border-radius:4px;margin-right:4px;}
.tdp-tl.sol{color:#9bd1ff;} .tdp-tl.gm{color:var(--amber);}
.tdp-pos{color:var(--green);} .tdp-neg{color:var(--red);} .tdp-muted{color:var(--text2);}
.tdp-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:5px;}
.tdp-badge.rug{background:rgba(255,59,92,.16);color:var(--red);}
.tdp-empty{padding:30px;text-align:center;color:var(--text2);font-size:12px;}
`;

const STATUS_OPTS = [
  { label: 'Tous', v: null }, { label: 'pending', v: 'pending' }, { label: 'running', v: 'running' },
  { label: 'done', v: 'done' }, { label: 'error', v: 'error' },
];
const KIND_OPTS = [{ label: 'Tous', v: null }, { label: 'creator', v: 'creator' }, { label: 'wallet', v: 'wallet' }];

const fmtAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');
const fmtTs = (iso) => {
  if (!iso) return <span className="muted">—</span>;
  try { return new Date(iso).toLocaleString('fr-FR', { hour12: false }); } catch { return iso; }
};

function DetailModal({ jobId, onClose, onRelaunched }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['bt-job', jobId],
    queryFn: () => getBacktestJob(jobId),
    refetchInterval: (q) => {
      const s = q?.state?.data?.status;
      return (s === 'pending' || s === 'running') ? 4000 : false;
    },
  });

  const [busy, setBusy] = useState(false);
  const [relErr, setRelErr] = useState(null);
  const relaunch = async () => {
    if (busy || !data) return;
    setBusy(true); setRelErr(null);
    try {
      let res;
      if (data.kind === 'cohort') {
        const ruleId = String(data.address || '').replace(/^rule:/, '');
        const p = data.params || {};
        const matrix = {
          tp_list: p.tp_list, size_list: p.size_list, exit_latency_ms: p.exit_latency_ms,
          stop_pct: p.stop_pct, max_picks: p.max_picks, pick_order: p.pick_order,
        };
        res = await postRuleBacktest(ruleId, matrix);
      } else {
        res = await postBacktestTarget({ kind: data.kind, address: data.address, params: data.params });
      }
      if (res?.job_id && res.job_id !== jobId) {
        onRelaunched && onRelaunched(res.job_id);   // bascule la modale sur le nouveau job
      } else if (res?.cached) {
        setRelErr('Résultat identique déjà en cache (cooldown) — pas de nouveau run.');
      }
    } catch (e) {
      setRelErr(e?.response?.data?.detail || e?.message || 'échec de la relance');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tdp-ovl" onClick={onClose}>
      <div className="tdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tdp-top">
          <div>
            <div className="tdp-title">Job #{jobId} {data ? <>· {data.kind} {fmtAddr(data.address)}<CopyBtn text={data.address} /></> : ''}</div>
            <div className="tdp-sub">{data?.status || '…'}</div>
            {relErr && <div className="tdp-sub" style={{ color: 'var(--amber,#ffb020)' }}>{relErr}</div>}
          </div>
          <div className="tdp-sp" />
          <button className="tdp-btn" disabled={busy || !data} onClick={relaunch}>relancer</button>
          <button className="tdp-back" onClick={onClose} style={{ marginLeft: 4 }}>✕</button>
        </div>
        <div className="tdp-body">
          {isLoading ? <div className="tdp-empty">Chargement…</div>
            : isError ? <div className="tdp-empty">Erreur : {error?.message || 'inconnue'}</div>
            : data?.status === 'done' && data?.result ? <BacktestMatrixView result={data.result} />
            : data?.status === 'error' ? <div className="tdp-empty" style={{ color: 'var(--red)' }}>Échec : {data?.error_msg || 'erreur inconnue'}</div>
            : <div className="tdp-empty">Backtest en cours… (statut : {data?.status})</div>}
        </div>
      </div>
    </div>
  );
}

function BacktestsPage() {
  const [status, setStatus] = useState(null);
  const [kind, setKind] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const i = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['bt-jobs', status, kind],
    queryFn: () => getBacktestJobs({ status, kind, limit: 200, skip: 0 }),
    refetchInterval: 5000,
  });

  const jobs = data?.jobs || [];
  const total = data?.total || 0;
  const t = clock.toLocaleTimeString('fr-FR', { hour12: false });

  const onDelete = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce backtest ?')) return;
    try { await deleteBacktestJob(jobId); refetch(); }
    catch (err) { window.alert('Échec suppression : ' + (err?.message || 'inconnue')); }
  };

  return (
    <div className="bt">
      <style>{CSS}</style>
      <NavRail />
      <div className="main">
        <div className="top">
          <span className="top-title">Backtests</span>
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b></div>
        </div>
        <div className="page">
          {isError && <div className="err-banner">Erreur chargement : {error?.message || 'inconnue'}</div>}

          <div className="bar-row">
            <span className="h1">File ({total})</span>
            <div className="sort-row">
              <span className="sort-lbl">Statut</span>
              {STATUS_OPTS.map(o => (
                <button key={o.label} className={`sort-btn ${status === o.v ? 'on' : ''}`} onClick={() => setStatus(o.v)}>{o.label}</button>
              ))}
              <span className="sort-lbl" style={{ marginLeft: 8 }}>Type</span>
              {KIND_OPTS.map(o => (
                <button key={o.label} className={`sort-btn ${kind === o.v ? 'on' : ''}`} onClick={() => setKind(o.v)}>{o.label}</button>
              ))}
            </div>
          </div>

          {isLoading ? <div className="loading">Chargement…</div>
            : jobs.length === 0 ? <div className="nodata">Aucun job — flagger une entité depuis Créateurs ou Wallets.</div>
            : (
              <div className="tbl-wrap">
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Statut</th>
                        <th>Entité</th>
                        <th>Params</th>
                        <th>Demandé par</th>
                        <th>Demandé</th>
                        <th>Terminé</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map(j => (
                        <tr key={j.job_id} style={{ cursor: 'pointer' }} onClick={() => setOpenJob(j.job_id)}>
                          <td><span className={`badge ${j.status}`}>{j.status}</span></td>
                          <td>
                            <span className="muted" style={{ fontSize: 9.5, marginRight: 5 }}>{j.kind}</span>
                            <span className="addr" title={j.address}>{fmtAddr(j.address)}</span>
                            <CopyBtn text={j.address} />
                            {j.address && (
                              <span style={{ marginLeft: 6 }} onClick={(e) => e.stopPropagation()}>
                                <a className="tdp-tl sol" href={`https://solscan.io/account/${j.address}`} target="_blank" rel="noopener noreferrer">SS</a>
                                <a className="tdp-tl gm" href={`https://gmgn.ai/sol/address/${j.address}`} target="_blank" rel="noopener noreferrer">GM</a>
                              </span>
                            )}
                          </td>
                          <td><span className="muted" style={{ fontSize: 10 }}>{j.params_summary || '—'}</span></td>
                          <td><span className="muted">{j.requested_by || '—'}</span></td>
                          <td>{fmtTs(j.requested_at)}</td>
                          <td>{fmtTs(j.finished_at)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button className="sort-btn" onClick={() => setOpenJob(j.job_id)}>détail</button>
                            <button className="sort-btn" style={{ marginLeft: 6 }} onClick={(e) => onDelete(j.job_id, e)}>supprimer</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      </div>
      {openJob != null && <DetailModal jobId={openJob} onClose={() => setOpenJob(null)} onRelaunched={(id) => setOpenJob(id)} />}
    </div>
  );
}

export default BacktestsPage;
